import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Spinner from '../components/Spinner'
import { AssociatedEntityType, ManagedPaymentEvent, parseManagedEvent } from '../state/Events'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { RADOM_COLORS } from '../util/Constants'
import styled from 'styled-components'
import TimeText from '../components/TimeText'
import BreadCrumbs from '../components/Breadcrumbs'
import { Container } from '../components/Animations'
import Radom, { Customer, DonationLinkOrder, ICheckoutSession, Invoice, IPaymentLink, ManagedBalanceLedger, PaymentLinkOrder, PaymentSession, PaymentSummary } from '../state/Radom'
import Table from '../components/Table'
import LinkExternal from '../components/LinkExternal'
import { ExternalLinkText } from './Events'
import CheckoutSessionDetails from '../components/CheckoutSessionDetails'
import PaymentSessionDetails from '../components/PaymentSessionDetails'
import { PaymentDetailsSection } from '../components/PaymentDetails'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Dots from '../icons/Dots'
import { PrimaryButton } from '../components/Button'
import Modal from '../components/Modal'
import { errorToast, shouldRound, trimZeros } from '../util/Util'
import RefundForm from '../components/RefundForm'
import RefundDetails from '../components/RefundDetails'
import _ from 'lodash'
import { getMethod } from '../util/Managed'
import { Chevron } from '../icons/Chevron'
import { Section, SectionContent, SectionItem } from '../components/Shared'

const SubTitle = styled.div`
  font-size: 14px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
`

const Payment = observer((): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')
  const [paymentEvent, setPaymentEvent] = useState<ManagedPaymentEvent>()
  const [isLoading, setIsLoading] = useState(false)
  const [paymentLinkOrder, setPaymentLinkOrder] = useState<PaymentLinkOrder>()
  const [paymentLink, setPaymentLink] = useState<IPaymentLink>()
  const [donationLinkOrder, setDonationLinkOrder] = useState<DonationLinkOrder>()
  const [checkoutSession, setCheckoutSession] = useState<ICheckoutSession>()
  const [paymentSession, setPaymentSession] = useState<PaymentSession>()
  const [invoice, setInvoice] = useState<Invoice>()
  const [isIncomplete, setIsIncomplete] = useState(false)
  const [isIncompleteModalOpen, setIsIncompleteModalOpen] = useState(false)
  const [isCompletingPayment, setIsCompletingPayment] = useState(false)
  const [refundModalOpen, setRefundModalOpen] = useState(false)
  const [customer, setCustomer] = useState<Customer>()
  const [generatationPDFloading, setGeneratationPDFloading] = useState(false)
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger>()
  const [paymentSummary, setPaymentSummary] = useState<{
    in: { amount: string | number, ticker: string, logo: string }
    out?: { amount: string | number, ticker: string, logo: string }
  }>({ in: { amount: '0', ticker: 'USDC', logo: '' } })

  const fetchPaymentData = async (id: string): Promise<void> => {
    setIsLoading(true)
    let paymentEvent: ManagedPaymentEvent
    let isFiatPayment: boolean
    let paymentSummary: PaymentSummary | null = null
    try {
      const payment = await Radom.getEvent(id)
      paymentEvent = parseManagedEvent(payment) as ManagedPaymentEvent
      paymentSummary = payment.eventData?.payment?.paymentSummary ??
          payment.eventData?.recurringPayment?.paymentSummary ??
          payment.eventData?.subscriptionPayment?.paymentSummary ??
          payment.eventData?.incompletePayment?.paymentSummary ??
          null
      isFiatPayment = !paymentSummary
      setPaymentSummary({
        in: {
          amount: paymentSummary?.grossAmount ??
        paymentEvent.amount,
          ticker: paymentEvent.paymentMethod.ticker,
          logo: paymentEvent.paymentMethod.logo ?? ''
        }
      })
      setPaymentEvent(paymentEvent)
      setIsIncomplete(paymentEvent.name === 'IncompletePaymentEvent')
    } catch {
      setIsLoading(false)
      errorToast('Failed to load payment')
      return
    }

    try {
      setPaymentLinkOrder(undefined)
      setCheckoutSession(undefined)
      setPaymentSession(undefined)
      setDonationLinkOrder(undefined)

      if (paymentEvent.associatedEntity.entityType === AssociatedEntityType.PaymentLink) {
        const paymentLinkOrder = await Radom.getPaymentLinkOrder(paymentEvent.associatedEntity.id)
        const paymentLink = await Radom.getPaymentLink(paymentLinkOrder.paymentLinkId)
        setPaymentLinkOrder(paymentLinkOrder)
        setPaymentLink(paymentLink)
      }

      if (paymentEvent.associatedEntity.entityType === AssociatedEntityType.PaymentSession) {
        const paymentSession = await Radom.getPaymentSession(paymentEvent.associatedEntity.id)
        setPaymentSession(paymentSession)
      }

      if (paymentEvent.associatedEntity.entityType === AssociatedEntityType.CheckoutSession) {
        const checkoutSession = await Radom.getCheckoutSession(paymentEvent.associatedEntity.id)
        if (
          checkoutSession.payment?.openBanking &&
          checkoutSession.payment?.openBanking.onrampGrossAmount &&
          checkoutSession.payment?.openBanking.onrampNetwork &&
          checkoutSession.payment?.openBanking.onrampToken &&
          isFiatPayment
        ) {
          const openBankingMethod = getMethod(
            checkoutSession.payment?.openBanking.onrampNetwork,
            checkoutSession.payment?.openBanking.onrampToken
          )
          setPaymentSummary({
            in: {
              amount: paymentSummary?.grossAmount ??
        paymentEvent.amount,
              ticker: paymentEvent.paymentMethod.ticker,
              logo: paymentEvent.paymentMethod.logo ?? ''
            },
            out: {
              amount: checkoutSession.payment?.openBanking.onrampGrossAmount,
              ticker: openBankingMethod.ticker,
              logo: openBankingMethod.logo ?? ''
            }
          })
        }
        setCheckoutSession(checkoutSession)
      }

      if (paymentEvent.associatedEntity.entityType === AssociatedEntityType.Invoice) {
        const invoice = await Radom.getInvoice(paymentEvent.associatedEntity.id)
        setInvoice(invoice)
      }

      if (paymentEvent.associatedEntity.entityType === AssociatedEntityType.DonationLink) {
        const donationLinkOrder = await Radom.getDonationLinkOrder(paymentEvent.associatedEntity.id)
        const paymentId = donationLinkOrder.payment.managed?.id
        if (paymentId) {
          const payment = await Radom.getPayment(paymentId)
          if (payment.customerId) {
            const customer = await Radom.getCustomer(payment.customerId)
            setCustomer(customer)
          }
        }
        setDonationLinkOrder(donationLinkOrder)
      }
    } catch {
      errorToast('Failed to load payment details')
    }

    try {
      const ledger = await Radom.listManagedBalanceLedger([paymentEvent.associatedEntity.id])
      if (ledger.length > 0) { setManagedBalanceLedger(ledger[0]) }
    } catch {
      errorToast('Failed to load ledger')
    }
    setIsLoading(false)
  }

  const debouncedFetchPaymentData = useMemo(() => _.debounce(fetchPaymentData, 300), [])

  const generateReceipt = async (): Promise<void> => {
    try {
      if (!params.id) return
      setGeneratationPDFloading(true)
      const data = await Radom.generateReceipt(params.id)
      if (data.url !== '') {
        window.open(data.url, '_blank')
      }
    } catch (err) {
      errorToast(err.reason || err.message || err.toString())
    } finally {
      setGeneratationPDFloading(false)
    }
  }

  useEffect(() => {
    if (!params.id) return
    debouncedFetchPaymentData(params.id)
    return () => debouncedFetchPaymentData.cancel()
  }, [params.id])

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (paymentEvent) {
    return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <BreadCrumbs items={
          source === 'transactions'
            ? [
              { item: 'Transactions', to: '/transactions' },
              { item: paymentEvent.id, to: `/payment/${paymentEvent.id}?source=transactions` }
            ]
            : [
              { item: 'Payments', to: '/payments' },
              { item: paymentEvent.id, to: '.' }
            ]} />

        {
          isIncomplete &&
          <Dropdown
            noChevron
            selectedContent={<Dots width={15} />}
            dropdownContent={
              <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                <DropdownItem onClick={() => setIsIncompleteModalOpen(true)}>Complete payment</DropdownItem>
              </div>
            }
          />
        }

        {
          !isIncomplete && (
            ((paymentSession || donationLinkOrder || paymentLinkOrder || checkoutSession) && paymentEvent.name !== 'RecurringPaymentEvent') && (
              <Dropdown
                noChevron
                selectedContent={<Dots width={15} />}
                dropdownContent={
                  <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                    {(paymentSession || donationLinkOrder) && (
                      <DropdownItem onClick={() => setRefundModalOpen(true)}>
                Refund payment
                      </DropdownItem>
                    )}
                    {(paymentLinkOrder || checkoutSession) && (
                      <DropdownItem onClick={generateReceipt}>
                        {generatationPDFloading ? <Spinner /> : 'Export PDF'}
                      </DropdownItem>
                    )}
                    {false && <DropdownItem>Send to email</DropdownItem>}
                  </div>
                }
              />
            )
          )
        }
      </div>

      {
        refundModalOpen &&
        <Modal visible={refundModalOpen} onClose={() => setRefundModalOpen(false)} title='Refund payment'>
          <div style={{ width: 500, padding: '0 30px 30px 30px' }}>
            <RefundForm
              paymentSession={paymentSession}
              donationLinkOrder={donationLinkOrder}
              onRefund={() => setRefundModalOpen(false)} />
          </div>
        </Modal>
      }

      <Section>
        <div style={{
          fontSize: '18px',
          paddingBottom: 5
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: '32px'
          }}>
            <img src={paymentSummary.in.logo} style={{ width: 32, height: 'auto', maxHeight: 32 }} />
            <span>
              {trimZeros(shouldRound(paymentSummary.in.ticker)
                ? Number(paymentSummary.in.amount).toFixed(2)
                : paymentSummary.in.amount.toString())
              }
            </span>
            <span style={{
              background: RADOM_COLORS.GRAY_MED,
              color: RADOM_COLORS.BLACK,
              borderRadius: 10,
              fontSize: 14,
              padding: '5px 10px',
              fontWeight: 400
            }}>{paymentSummary.in.ticker}</span>
            {paymentSummary.out && <>
              <Chevron fill={RADOM_COLORS.GRAY_DARKER} style={{ width: 16, height: 16, transform: 'rotate(-90deg)' }} />
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '32px'
              }}>
                <img src={paymentSummary.out.logo } style={{ width: 32, height: 'auto', maxHeight: 32 }} />
                <span>
                  {trimZeros(shouldRound(paymentSummary.out.ticker)
                    ? Number(paymentSummary.out.amount).toFixed(2)
                    : paymentSummary.out.amount.toString())
                  }
                </span>
                <span style={{
                  background: RADOM_COLORS.GRAY_MED,
                  color: RADOM_COLORS.BLACK,
                  borderRadius: 10,
                  fontSize: 14,
                  padding: '5px 10px',
                  fontWeight: 400
                }}>{paymentSummary.out.ticker}</span>
              </div></>}
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          flexWrap: 'wrap',
          gap: 30
        }}>
          <div>
            <SubTitle>Type</SubTitle>
            <div>{paymentEvent.label}</div>
          </div>
          <div>
            <SubTitle>Associated entity</SubTitle>
            <div>
              {
                paymentEvent.associatedEntity &&
                paymentEvent.associatedEntity.entityType !== AssociatedEntityType.PaymentSession &&
                paymentEvent.associatedEntity.entityType !== AssociatedEntityType.PaymentLink &&
                <ExternalLinkText
                  onClick={e => e.stopPropagation()}
                  href={paymentEvent.associatedEntity.link}
                  target="_blank" rel="noreferrer">
                  <span>{AssociatedEntityType[paymentEvent.associatedEntity.entityType]}</span>
                  <span>{paymentEvent.associatedEntity.id}</span>
                  <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
                </ExternalLinkText>
              }
              {
                paymentEvent.associatedEntity &&
                paymentEvent.associatedEntity.entityType === AssociatedEntityType.PaymentLink &&
                <>
                  {
                    paymentLink
                      ? <ExternalLinkText
                        onClick={e => e.stopPropagation()}
                        href={`/${paymentLink.isPresale ? 'presales' : 'payment_links'}/${paymentLink.id}`}
                        target="_blank" rel="noreferrer">
                        <span>{paymentLink.isPresale ? 'PresaleLink' : 'PaymentLink'}</span>
                        <span>{paymentEvent.associatedEntity.id}</span>
                        <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
                      </ExternalLinkText>
                      : '-'
                  }
                </>
              }
              {
                paymentEvent.associatedEntity &&
                paymentEvent.associatedEntity.entityType === AssociatedEntityType.PaymentSession &&
                <div style={{ display: 'flex', gap: 3 }}>
                  <span>{AssociatedEntityType[paymentEvent.associatedEntity.entityType]}</span>
                  <span>{paymentEvent.associatedEntity.id}</span>
                </div>
              }
            </div>
          </div>
          <div>
            <SubTitle>Transaction date</SubTitle>
            <div>
              <TimeText textColor={RADOM_COLORS.BLACK} date={paymentEvent.date} />
            </div>
          </div>

          {
            customer &&
            <div>
              <SubTitle>Customer</SubTitle>
              <Link to={`/customer/${customer?.id}`} style={{ fontSize: 14, color: RADOM_COLORS.BLUE_MED }}>
                <span>{customer.name || customer.email}</span>
              </Link>
            </div>
          }
        </div>
      </Section>

      {
        managedBalanceLedger &&
        <Section>
          <SubTitle style={{ fontSize: 18 }}>
                Ledger
          </SubTitle>
          <SectionContent>
            <SectionItem>
              <SubTitle>Balance Before</SubTitle>
              <div style={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                <span>
                  {
                    trimZeros(shouldRound(paymentSummary.out?.ticker ?? paymentSummary.in.ticker)
                      ? Number(managedBalanceLedger.balanceBefore).toFixed(2)
                      : managedBalanceLedger.balanceBefore)
                  }
                </span>
                <span style={{ color: RADOM_COLORS.GRAY4 }}>
                  {paymentSummary.out?.ticker ?? paymentSummary.in.ticker} on {managedBalanceLedger.network}
                </span>
              </div>
            </SectionItem>
            <SectionItem>
              <SubTitle>Balance After</SubTitle>
              <div style={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                <span>
                  {
                    trimZeros(shouldRound(paymentSummary.out?.ticker ?? paymentSummary.in.ticker)
                      ? Number(managedBalanceLedger.balanceAfter).toFixed(2)
                      : managedBalanceLedger.balanceAfter)
                  }
                </span>
                <span style={{ color: RADOM_COLORS.GRAY4 }}>
                  {paymentSummary.out?.ticker ?? paymentSummary.in.ticker} on {managedBalanceLedger.network}
                </span>
              </div>
            </SectionItem>
          </SectionContent>
        </Section>
      }

      {
        paymentLinkOrder && paymentLinkOrder.orderData.length > 0 &&
        <Section>
          <SubTitle style={{
            fontSize: 18,
            paddingBottom: 5
          }}>Order details</SubTitle>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>

            <div style={{ fontSize: 14 }}>
              <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Customer input</SubTitle>
              <Table
                headers={[
                  <span key="productType">Input type</span>,
                  <span key="quantity">Value</span>
                ]}
                items={
                  paymentLinkOrder.orderData.map(p => [
                    <span key="type">{p.key}</span>,
                    <span key="quantity">{p.value}</span>
                  ])
                }
                isLoading={false}
              />
            </div>

          </div>
        </Section>
      }

      {
        donationLinkOrder && donationLinkOrder.orderData.length > 0 &&
        <Section>
          <SubTitle style={{
            fontSize: 18,
            paddingBottom: 5
          }}>Donation details</SubTitle>

          <div style={{ fontSize: 14 }}>
            <SubTitle style={{ fontSize: 14, paddingBottom: 10 }}>Customer input</SubTitle>
            <Table
              headers={[
                <span key="productType">Input type</span>,
                <span key="quantity">Value</span>
              ]}
              items={
                donationLinkOrder.orderData.map(p => {
                  const valueElement = p.value.toString()

                  return [
                    <span key={`type-${p.key}`}>{p.key}</span>,
                    <span key={`quantity-${p.key}`}>{valueElement}</span>
                  ]
                })
              }
              isLoading={false}
            />
          </div>
        </Section>
      }

      {
        checkoutSession &&
        <CheckoutSessionDetails checkoutSession={checkoutSession} netPayment={paymentEvent.amount} />
      }

      {
        paymentSession &&
        <PaymentSessionDetails paymentSession={paymentSession} netPayment={paymentEvent.amount} />
      }

      {
        invoice?.payment?.managed &&
        <PaymentDetailsSection payment={invoice.payment} netPayment={paymentEvent.amount} />
      }

      {
        paymentLinkOrder?.payment?.managed &&
        <PaymentDetailsSection payment={paymentLinkOrder.payment} netPayment={paymentEvent.amount} />
      }

      {
        donationLinkOrder?.payment?.managed &&
        <PaymentDetailsSection payment={donationLinkOrder.payment} netPayment={paymentEvent.amount} />
      }

      {
        paymentLinkOrder?.payment?.managedTokenDelegatePayment &&
        <PaymentDetailsSection
          payment={paymentLinkOrder.payment}
          netPayment={paymentEvent.amount} />
      }

      {
        donationLinkOrder && donationLinkOrder.refunds.length > 0 &&
        <RefundDetails refunds={donationLinkOrder.refunds} />
      }

      {
        isIncompleteModalOpen &&
        <Modal
          title='Complete payment'
          visible={isIncompleteModalOpen}
          onClose={() => setIsIncompleteModalOpen(false)}
          innerStyles={{ width: '400px', minHeight: 'auto' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div style={{ padding: '20px 30px' }}>
              <span style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKEST, whiteSpace: 'pre-wrap' }}>
                Are you sure you would like to mark this payment as complete?
                This will trigger any post-payment actions and is irreversible.
              </span>
            </div>
            <div style={{ padding: 30 }}>
              <PrimaryButton
                isLoading={isCompletingPayment}
                style={{ width: '100%' }}
                onClick={async () => {
                  setIsCompletingPayment(true)
                  try {
                    await Radom.completePayment({ eventId: params.id })
                    window.location.reload()
                  } catch (err) {
                    errorToast(err.reason || err.message || err.toString())
                  }
                  setIsCompletingPayment(false)
                }}>
                Complete payment
              </PrimaryButton>
            </div>
          </div>
        </Modal>
      }
    </Container>
  }

  return <div>
    <span>Order not found</span>
  </div>
})

export default Payment
